import React from 'react';
import {
  View,
  Text,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import Animated, { FadeInDown } from 'react-native-reanimated';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  ArrowLeft,
  Rocket,
  TrendingUp,
  Shield,
  CheckCircle2,
  Clock,
  Mail,
  MessageSquare,
  FolderOpen,
  Eye,
  LogIn,
  Star,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import {
  useAdminRole,
  useAdminUserDetail,
  useAdminUserActivity,
  hasPermission,
} from '@/lib/admin';
import { AppLayout } from '@/components/AppLayout';
import { WebCard, WebGrid } from '@/components/web/WebAppShell';
import { UserActivityTimeline } from '@/components/admin/UserActivityTimeline';
import { UserEngagementChart } from '@/components/admin/UserEngagementChart';

const ROLE_ICON: Record<string, typeof Rocket> = {
  startup: Rocket,
  investor: TrendingUp,
  admin: Shield,
};

const ROLE_COLOR: Record<string, string> = {
  startup: '#34C759',
  investor: '#007AFF',
  admin: '#FF9500',
};

function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return 'Never';
  const now = Date.now();
  const diff = now - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function UserDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const colors = useThemeColors();

  const { data: adminRole } = useAdminRole();
  const { data: user, isLoading } = useAdminUserDetail(id);
  const { data: activity } = useAdminUserActivity(id);

  const canSeeAnalytics = hasPermission(adminRole, 'users_analytics');

  if (isLoading || !user) {
    return (
      <AppLayout
        title="User Detail"
        breadcrumbs={[
          { label: 'Admin', href: '/(admin)/admin-dashboard' },
          { label: 'Users', href: '/(admin)/users' },
          { label: id ?? '' },
        ]}
      >
        <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
          <ActivityIndicator size="large" color={colors.primary[600]} />
        </View>
      </AppLayout>
    );
  }

  const RoleIcon = ROLE_ICON[user.role] ?? Shield;
  const roleColor = ROLE_COLOR[user.role] ?? '#8E8E93';
  const initial = user.email.charAt(0).toUpperCase();

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <AppLayout
      title="User Detail"
      breadcrumbs={[
        { label: 'Admin', href: '/(admin)/admin-dashboard' },
        { label: 'Users', href: '/(admin)/users' },
        { label: user.email },
      ]}
    >
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={isWeb ? [] : ['top', 'bottom']}>
        <ScrollView className="flex-1" showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 32 }}>
          {/* Back button */}
          <AnimatedPressable
            onPress={() => router.back()}
            className="flex-row items-center gap-2 mb-4"
          >
            <ArrowLeft size={18} color={colors.primary[600]} />
            <Text style={{ color: colors.primary[600] }} className="text-sm font-semibold">
              Back to Users
            </Text>
          </AnimatedPressable>

          {/* Profile card */}
          <Animated.View entering={FadeInDown.delay(100).duration(400)} className="mb-4">
            <WebCard>
              <View className="flex-row items-center gap-4">
                {/* Avatar */}
                <View
                  className="w-14 h-14 rounded-2xl items-center justify-center"
                  style={{ backgroundColor: `${roleColor}20` }}
                >
                  <Text style={{ color: roleColor }} className="text-xl font-bold">
                    {initial}
                  </Text>
                </View>

                <View className="flex-1">
                  <View className="flex-row items-center gap-2">
                    <Mail size={14} color={colors.text.tertiary} />
                    <Text style={{ color: colors.text.primary }} className="text-sm font-semibold">
                      {user.email}
                    </Text>
                  </View>
                  <View className="flex-row items-center gap-3 mt-2">
                    <View
                      className="flex-row items-center gap-1.5 px-2.5 py-1 rounded-lg"
                      style={{ backgroundColor: `${roleColor}15` }}
                    >
                      <RoleIcon size={12} color={roleColor} />
                      <Text style={{ color: roleColor }} className="text-xs font-bold capitalize">
                        {user.role}
                      </Text>
                    </View>
                    <View className="flex-row items-center gap-1">
                      {user.onboarding_completed ? (
                        <CheckCircle2 size={12} color="#34C759" />
                      ) : (
                        <Clock size={12} color="#FF9500" />
                      )}
                      <Text style={{ color: colors.text.tertiary }} className="text-xs">
                        {user.onboarding_completed ? 'Onboarded' : 'Pending'}
                      </Text>
                    </View>
                  </View>
                  <View className="flex-row items-center gap-4 mt-2">
                    <Text style={{ color: colors.text.tertiary }} className="text-[10px]">
                      Joined {formatDate(user.created_at)}
                    </Text>
                    {user.last_active && (
                      <Text style={{ color: colors.text.tertiary }} className="text-[10px]">
                        Active {formatTimeAgo(user.last_active)}
                      </Text>
                    )}
                  </View>
                </View>
              </View>
            </WebCard>
          </Animated.View>

          {/* Key metrics row */}
          <Animated.View entering={FadeInDown.delay(200).duration(400)} className="mb-4">
            <WebGrid columns={{ mobile: 2, tablet: 2, desktop: 4 }} gap={12}>
              <MetricCard
                icon={user.role === 'startup'
                  ? <Star size={18} color={user.score && user.score >= 70 ? '#34C759' : user.score && user.score >= 40 ? '#FF9500' : colors.primary[600]} />
                  : <Eye size={18} color="#007AFF" />}
                label={user.role === 'startup' ? 'Inverio Score' : 'Dealflow Views'}
                value={user.role === 'startup' ? (user.score ?? '—') : user.data_room_views}
                colors={colors}
              />
              <MetricCard
                icon={<MessageSquare size={18} color="#00B8B8" />}
                label="Messages"
                value={user.message_count}
                colors={colors}
              />
              <MetricCard
                icon={<FolderOpen size={18} color="#FF9500" />}
                label={user.role === 'startup' ? 'Data Room Files' : 'Rooms Viewed'}
                value={user.role === 'startup' ? user.data_room_files : user.data_room_views}
                colors={colors}
              />
              <MetricCard
                icon={<LogIn size={18} color="#007AFF" />}
                label="Logins"
                value={user.login_count}
                colors={colors}
              />
            </WebGrid>
          </Animated.View>

          {/* Activity chart (analyst/super_admin) */}
          {canSeeAnalytics && activity?.activityByDay && activity.activityByDay.length > 0 && (
            <Animated.View entering={FadeInDown.delay(300).duration(400)} className="mb-4">
              <WebCard>
                <UserEngagementChart data={activity.activityByDay} />
              </WebCard>
            </Animated.View>
          )}

          {/* Activity timeline */}
          {activity?.recentEvents && activity.recentEvents.length > 0 && (
            <Animated.View entering={FadeInDown.delay(400).duration(400)}>
              <WebCard>
                <Text style={{ color: colors.text.primary }} className="text-sm font-bold mb-3">
                  Recent Activity
                </Text>
                <UserActivityTimeline events={activity.recentEvents} />
              </WebCard>
            </Animated.View>
          )}
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
}

function MetricCard({
  icon,
  label,
  value,
  colors,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <WebCard>
      <View className="items-center py-1">
        {icon}
        <Text style={{ color: colors.text.primary }} className="text-xl font-bold mt-2">
          {value}
        </Text>
        <Text style={{ color: colors.text.secondary }} className="text-xs font-medium mt-0.5">
          {label}
        </Text>
      </View>
    </WebCard>
  );
}
